import { useEffect, useRef, useState } from "react";
import { addDaysIso, formatDueLabel, nextWeekStartIso, todayIso, weekendStartIso } from "@/lib/dates";

interface Props {
  value: string | null;
  onChange: (next: string | null) => void;
  onOpenChange?: (open: boolean) => void;
}

export default function DueDatePicker({ value, onChange, onOpenChange }: Props) {
  const [open, setOpen] = useState(false);
  const [dateDraft, setDateDraft] = useState(value ?? "");
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDateDraft(value ?? "");
  }, [value]);

  // Notify parent on real open/close transitions; skip mount to avoid a
  // spurious false on first render. Use a ref so an inline onOpenChange
  // identity change doesn't refire the effect.
  const onOpenChangeRef = useRef(onOpenChange);
  useEffect(() => {
    onOpenChangeRef.current = onOpenChange;
  });
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    onOpenChangeRef.current?.(open);
  }, [open]);

  // onBlur on the date input doesn't fire when the popover unmounts on
  // outside-click, so the draft would otherwise be silently dropped.
  const commitRef = useRef<() => void>(() => {});
  commitRef.current = () => {
    const next = dateDraft || null;
    if (next === (value ?? null)) return;
    onChange(next);
  };

  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        commitRef.current();
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setDateDraft(value ?? "");
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handle);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", handle);
      document.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, value]);

  function pick(next: string | null) {
    onChange(next);
    setOpen(false);
  }

  function commitDateDraft() {
    commitRef.current();
  }

  const label = value ? formatDueLabel(value).text : "Дата выполнения";

  return (
    <div className="relative" ref={wrapRef}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="details-field">
        <svg
          className={`w-4 h-4 flex-shrink-0 ${value ? "text-blue-500" : "text-gray-400"}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <span>{label}</span>
        {value && (
          <span
            role="button"
            tabIndex={0}
            aria-label="Очистить срок"
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onChange(null);
              }
            }}
            className="details-field-clear ml-auto"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </span>
        )}
      </button>
      {open && (
        <div className="details-popover" data-testid="due-popover">
          <button type="button" onClick={() => pick(todayIso())} className="details-popover-item">
            Сегодня
          </button>
          <button type="button" onClick={() => pick(addDaysIso(todayIso(), 1))} className="details-popover-item">
            Завтра
          </button>
          <button type="button" onClick={() => pick(weekendStartIso())} className="details-popover-item">
            Выходные
          </button>
          <button type="button" onClick={() => pick(nextWeekStartIso())} className="details-popover-item">
            Следующая неделя
          </button>
          <input
            type="date"
            value={dateDraft}
            onChange={(e) => setDateDraft(e.target.value)}
            onBlur={commitDateDraft}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                pick(dateDraft || null);
              }
            }}
            className="details-popover-search"
            aria-label="Указать дату"
          />
          {value && (
            <button
              type="button"
              onClick={() => pick(null)}
              className="details-popover-item text-red-600 dark:text-red-400"
            >
              Убрать
            </button>
          )}
        </div>
      )}
    </div>
  );
}
